import React, { useEffect, useState, useRef } from "react";
import "../../../styles/desktop/CampoLuzes.css";
import { LightGroup } from "../../Classes/LightGroup";
import Ligado     from "../../../assets/img/Ligado.png";
import Desligado  from "../../../assets/img/Desligado.png";
import {
  getLuzesStatusFlex8,
  statusDasLuzesFlex8
} from "../../../API/basic/FeedBackLuzes";
import { sendCommandFlex8 } from "../../../API/basic/AcionamentoFlex8";
import LegendaAutomatica   from "../LegendaAutomatica/LegendaAutomatica";
import { iluminacaoConfig } from "../../../configs/iluminação";

const lightGroupsConfig = iluminacaoConfig;
const delay = ms => new Promise(r => setTimeout(r, ms));

/* --------------------------------------------------------------------- */

const GroupLightComponent = ({
  startIndex = 0,
  endIndex   = 20,
  groupIds   = [],
  pageTitle  = "Página 1",
  pageId     = "1"
}) => {
  /* -------------------- filtros / dados visíveis -------------------- */
  const visibleGroups =
    groupIds.length > 0
      ? lightGroupsConfig.filter(g => groupIds.includes(g.id))
      : lightGroupsConfig.slice(startIndex, endIndex);

  /* ---------------- refs e estados dinâmicos ------------------------ */
  const createInitialRefs = () => {
    const refs = {};
    visibleGroups.forEach(g => (refs[g.id] = React.createRef()));
    return refs;
  };

  const createInitialStates = () => {
    const st = {};
    visibleGroups.forEach(g => (st[g.id] = false));
    return st;
  };

  const groupRefs          = useRef(createInitialRefs());
  const [imageStates,  setImageStates]  = useState(createInitialStates());
  const [fakeStates,   setFakeStates]   = useState({}); // Apenas para grupos base
  const [acionarAllText, setAcionarAllText] = useState("Ligar");
  const [isBlocked,    setIsBlocked]    = useState(false);
  const [isSequencing, setIsSequencing] = useState(false);

  // Ref para armazenar os timers de fakeState
  const fakeStateTimers = useRef({});

  /* --------------------- polling de status -------------------------- */
  useEffect(() => {
    const fetchData = () => {
      if (isBlocked) return;
      
      getLuzesStatusFlex8()
        .then(() => updateLightGroup(statusDasLuzesFlex8))
        .catch(err => console.error("Erro polling:", err));
    };

    fetchData();
    const id = setInterval(fetchData, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlocked]);

  /* --------------------- helpers ------------------------------------ */
  /** fakeState temporário - APENAS para grupos base, expira em 10 segundos */
  const setFakeStateTemporario = (groupBaseId, estado) => {
    // Define o fakeState
    setFakeStates(prev => ({ ...prev, [groupBaseId]: estado }));

    // Limpa timer anterior se existir
    if (fakeStateTimers.current[groupBaseId]) {
      clearTimeout(fakeStateTimers.current[groupBaseId]);
    }

    // Cria novo timer para expirar em 10 segundos
    fakeStateTimers.current[groupBaseId] = setTimeout(() => {
      setFakeStates(prev => {
        const newFakeStates = { ...prev };
        delete newFakeStates[groupBaseId];
        return newFakeStates;
      });
      delete fakeStateTimers.current[groupBaseId];
      console.log(`FakeState expirado para grupo ${groupBaseId}`);
    }, 10000); // 10 segundos fixos

    console.log(`FakeState definido para grupo ${groupBaseId}: ${estado} (expira em 10s)`);
  };

  /* --------------------- update vindo do servidor ------------------- */
  const updateLightGroup = data => {
    if (!Array.isArray(data)) return;

    const newStates = { ...imageStates };
    visibleGroups.forEach(group => {
      const mod = +group.modulo - 1;
      const idx = +group.idx    - 1;
      if (data[mod]?.dados?.[idx] !== undefined)
        newStates[group.id] = data[mod].dados[idx] === "ON";
    });
    setImageStates(newStates);
  };

  /* --------------------- atualiza texto do botão ------------------- */
  useEffect(() => {
    const estadosAtuais = visibleGroups.map(g => 
      fakeStates[g.id] !== undefined ? fakeStates[g.id] : imageStates[g.id]
    );
    const todas = estadosAtuais.every(s => s);
    setAcionarAllText(todas ? "Desligar" : "Ligar");
  }, [fakeStates, imageStates, visibleGroups]);

  /* --------------------- clique individual -------------------------- */
  const handleToggleLightGroup = (idComIndice, novoEstado) => {
    if (isSequencing) return;

    const baseId = idComIndice.split("-")[0];
    const grupo  = visibleGroups.find(g => g.id === baseId);
    if (!grupo) return;

    // Define fakeState temporário apenas para o grupo base
    // NÃO atualiza imageStates - ele deve vir apenas do polling
    setFakeStateTemporario(baseId, novoEstado);
  };

  /* --------------------- ACIONAR / DESLIGAR TODOS ------------------- */
  const handleAcionarAll = async () => {
    if (isSequencing) return;
    const ligarTudo = Object.values(imageStates).some(s => !s);

    setIsSequencing(true);
    setIsBlocked(true);

    const gruposAlvo = visibleGroups.filter(g => {
      const temFakeON  = fakeStates[g.id] === true;
      const temFakeOFF = fakeStates[g.id] === false;
      return ligarTudo
        ? (!temFakeON && !imageStates[g.id])
        : (temFakeON || imageStates[g.id] || temFakeOFF);
    });

    try {
      if (ligarTudo) {
        for (const g of gruposAlvo) {
          // Define fakeState temporário
          // NÃO atualiza imageStates - ele deve vir apenas do polling
          setFakeStateTemporario(g.id, true);
          
          await sendCommandFlex8(g.descricao, {
            modulo: g.modulo,
            idx:    g.idx,
            status: "ON"
          });
          await delay(1000);
        }
      } else {
        for (let i = gruposAlvo.length - 1; i >= 0; i--) {
          const g = gruposAlvo[i];
          
          // Define fakeState temporário
          // NÃO atualiza imageStates - ele deve vir apenas do polling
          setFakeStateTemporario(g.id, false);
          
          await sendCommandFlex8(g.descricao, {
            modulo: g.modulo,
            idx:    g.idx,
            status: "OFF"
          });
          await delay(1000);
        }
      }
    } catch (e) {
      console.error("Falha acionarAll:", e);
      // Em caso de erro, limpa os fakeStates imediatamente
      gruposAlvo.forEach(g => {
        if (fakeStateTimers.current[g.id]) {
          clearTimeout(fakeStateTimers.current[g.id]);
          delete fakeStateTimers.current[g.id];
        }
        setFakeStates(prev => {
          const newFakeStates = { ...prev };
          delete newFakeStates[g.id];
          return newFakeStates;
        });
      });
    } finally {
      setTimeout(() => {
        setIsBlocked(false);
        setIsSequencing(false);
      }, 5000);
    }
  };

  /* --------------------- expansão p/ instâncias --------------------- */
  const expandGroupToInstances = g => {
    const base = {
      id:  g.id,
      descricao: g.descricao,
      modulo: g.modulo,
      idx:    g.idx,
      onCommand:  { modulo: g.modulo, idx: g.idx, status: "ON" },
      offCommand: { modulo: g.modulo, idx: g.idx, status: "OFF" },
      lightType: g.lightType,
      ledColor:  g.ledColor,
      ledMode:   g.ledMode,
      ledPath:   g.ledPath,
      ledStrokeWidth: g.ledStrokeWidth
    };

    return (g.instances || []).map((inst, i) => ({
      ...base,
      ...inst,
      isLedStrip: inst.isLedStrip || g.lightType === "led",
      instanceIndex: i
    }));
  };

  /* --------------------- limpar timers na desmontagem --------------- */
  useEffect(() => {
    const timers = fakeStateTimers.current;
    return () => {
      // Limpa todos os timers ao desmontar o componente
      Object.values(timers).forEach(timer => {
        clearTimeout(timer);
      });
    };
  }, []);

  /* --------------------- JSX ---------------------------------------- */
  return (
    <div className="group" id={`campoGroup-pagina${pageId}`}>
      {visibleGroups
        .flatMap(expandGroupToInstances)
        .map(inst => {
          const groupId      = `${inst.id}-${inst.instanceIndex}`;
          const baseId       = inst.id;
          
          // PRIORIDADE: fakeState do grupo base > imageState do grupo base
          const estadoVisual = fakeStates[baseId] !== undefined 
            ? fakeStates[baseId] 
            : imageStates[baseId];

          return (
            <LightGroup
              key={groupId}
              groupId={groupId}
              className="light_button"
              descricao={inst.descricao}
              imageUrl={estadoVisual ? Ligado : Desligado}
              onCommand={inst.onCommand}
              offCommand={inst.offCommand}
              x={inst.x}
              y={inst.y}
              width={inst.width}
              height={inst.height}
              circleNumber={inst.circleNumber}
              imageState={estadoVisual}
              onToggleLightGroup={handleToggleLightGroup}
              isLedStrip={inst.isLedStrip}
              ledPath={inst.ledPath}
              ledColor={inst.ledColor}
              ledMode={inst.ledMode}
              ledStrokeWidth={inst.ledStrokeWidth}
              ref={groupRefs.current[inst.id]}
            />
          );
        })}

      <LegendaAutomatica
        groupRefs={groupRefs}
        fakeStates={fakeStates}
        imageStates={imageStates}
        handleToggleLightGroup={handleToggleLightGroup}
        visibleGroups={visibleGroups}
        sendCommand={sendCommandFlex8}
        className="campo-clickable-element"
      />

      <button
        disabled={isSequencing}
        className={`bntAcionarAll${acionarAllText === "Desligar" ? "-ativo" : ""}`}
        onClick={handleAcionarAll}
      >
        {isSequencing ? "Aguarde…" : `${acionarAllText} luzes`}
      </button>
    </div>
  );
};

export default React.memo(GroupLightComponent);